/**
 * CommitErrorPanel — Wave 7 commit-pipeline UI.
 *
 * Renders when `cart.status === 'pipeline-error'`. Surfaces:
 *   - The step that failed (humanized via commitStepLabel — never raw key)
 *   - The error code resolved against the SystemErrorCodes catalog
 *     (`/v3/config/system-error-codes`) — humanized label + suggested next
 *     action so the FSR has a non-technical path forward
 *   - Retry button → `client.retail.carts.commitRetry(cartId)` (re-uses
 *     idempotency keys; successful steps short-circuit per design §5)
 *   - Abort button → opens existing `CloseCartModal` pre-filled with the
 *     `System-Error` resolution code + the failing error code so the
 *     resolution-code workflow stays consistent
 *
 * Hard rules (Bill 2026-04-24):
 *   - No raw cart IDs / GUIDs / step keys in user-facing text.
 *   - No raw exception messages — humanized labels only.
 */

import { useEffect, useState } from 'react';
import type {
  CartDto,
  CommitOutcome,
  CommitPipelineStepKey,
  CommitStatus,
  SystemErrorCode,
} from '@retail-ops/shared-types';
import { commitStepLabel } from '@retail-ops/shared-types';
import { ApiError } from '@retail-ops/api-client';
import { Banner, Button } from '@/components/primitives';
import { useApi } from './hooks/useApi';
import { CloseCartModal } from './CloseCartModal';

export interface CommitErrorPanelProps {
  cart: CartDto;
  /**
   * Latest live status from `/commit/status` — drives which step shows as
   * failed + carries the fresh error code/message. Optional because callers
   * may render the panel from cached cart state alone.
   */
  status?: CommitStatus;
  /** Fired after a successful retry so the parent can transition state. */
  onRetried?: (outcome: CommitOutcome) => void;
  /** Fired after the cart is closed via the abort path. */
  onAborted?: (cart: CartDto) => void;
}

interface FailedStepSummary {
  /** Humanized label (never the raw key). */
  label: string;
  /** Closed-list code reference. Used for catalog lookup; never displayed raw. */
  errorCode?: string;
  /** Humanized error message (when present). */
  errorMessage?: string;
  /** Step key — used internally only for stable dedupe. Never rendered. */
  stepKey?: CommitPipelineStepKey;
}

/** Pull the failed step out of the live status snapshot. */
function summarizeFailedStep(
  cart: CartDto,
  status: CommitStatus | undefined,
): FailedStepSummary {
  const failed = status?.steps?.find((s) => s.status === 'failed');
  if (failed) {
    return {
      label: commitStepLabel(failed.stepKey),
      ...(failed.errorCode ? { errorCode: failed.errorCode } : {}),
      ...(failed.errorMessage ? { errorMessage: failed.errorMessage } : {}),
      stepKey: failed.stepKey,
    };
  }
  // Fall back to the cart-level last error fields when /commit/status hasn't
  // populated the per-step rows yet (race between transition + first poll).
  return {
    label: 'a commit step',
    ...(cart.errorCode ? { errorCode: cart.errorCode } : {}),
    ...(status?.lastPipelineErrorCode ? { errorCode: status.lastPipelineErrorCode } : {}),
    ...(cart.lastPipelineErrorMsg ? { errorMessage: cart.lastPipelineErrorMsg } : {}),
    ...(status?.lastPipelineErrorMsg ? { errorMessage: status.lastPipelineErrorMsg } : {}),
  };
}

export function CommitErrorPanel({
  cart,
  status,
  onRetried,
  onAborted,
}: CommitErrorPanelProps): JSX.Element {
  const { client, makeSignal } = useApi();
  const [errorCodes, setErrorCodes] = useState<ReadonlyArray<SystemErrorCode>>([]);
  const [retryState, setRetryState] = useState<
    | { kind: 'idle' }
    | { kind: 'submitting' }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });
  const [abortOpen, setAbortOpen] = useState(false);

  // Lazy-load the SystemErrorCodes catalog so we can humanize the code.
  useEffect(() => {
    let cancelled = false;
    void client.retail.config.systemErrorCodes({ signal: makeSignal() }).then(
      (res) => {
        if (cancelled) return;
        const anyRes = res as unknown as { success?: boolean; data?: SystemErrorCode[] };
        if (anyRes?.success !== false && anyRes?.data) {
          setErrorCodes(anyRes.data);
        }
      },
      () => {
        // Non-fatal — fallback to a generic humanization below.
      },
    );
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const failed = summarizeFailedStep(cart, status);
  const matchedCatalog = failed.errorCode
    ? errorCodes.find((ec) => ec.code === failed.errorCode)
    : undefined;

  async function handleRetry(): Promise<void> {
    setRetryState({ kind: 'submitting' });
    try {
      // NO abort signal — deliberately. The status poll flips this panel out
      // (unmount) seconds after the retry starts, and useApi's unmount cleanup
      // aborts every signal it minted; aborting a state-changing banking POST
      // cancelled the commit pipeline mid-Core-write (2026-07-02 incident).
      // The server is abort-immune now too, but a state-changing call should
      // never carry an unmount-scoped signal in the first place.
      const res = await client.retail.carts.commitRetry(cart.cartId);
      if (!res.success) {
        setRetryState({
          kind: 'error',
          message: res.error ?? 'Could not retry the commit. Try aborting and restarting the cart.',
        });
        return;
      }
      setRetryState({ kind: 'idle' });
      onRetried?.(res);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      const message = ApiError.isApiError(err)
        ? err.message
        : err instanceof Error
          ? err.message
          : 'Unexpected error.';
      setRetryState({ kind: 'error', message });
    }
  }

  return (
    <section
      aria-labelledby="commit-err-h"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-5)',
        padding: 'var(--sp-7)',
        background: 'var(--surface-card)',
        border: `1px solid var(--pcb-red)`,
        borderRadius: 'var(--r-md)',
      }}
    >
      <header style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
        <h2
          id="commit-err-h"
          style={{
            margin: 0,
            fontSize: 16,
            color: 'var(--pcb-blue-dark)',
            fontWeight: 600,
          }}
        >
          Commit hit a snag
        </h2>
        <div style={{ fontSize: 13, color: 'var(--ink-700)' }}>
          The pipeline stopped at <strong>{failed.label}</strong>. Successful steps before this point
          are preserved — retrying picks up where it left off.
        </div>
      </header>

      {(matchedCatalog || failed.errorMessage) && (
        <Banner tone="err" title={matchedCatalog?.label ?? 'Error details'}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            {matchedCatalog?.sourceSystem && (
              <div style={{ fontSize: 12 }}>
                <strong>Source:</strong> {matchedCatalog.sourceSystem}
              </div>
            )}
            {matchedCatalog?.description && (
              <div style={{ fontSize: 12 }}>{matchedCatalog.description}</div>
            )}
            {!matchedCatalog && failed.errorMessage && (
              <div style={{ fontSize: 12 }}>{failed.errorMessage}</div>
            )}
            {matchedCatalog?.suggestedNextAction && (
              <div style={{ fontSize: 12 }}>
                <strong>Next step:</strong> {matchedCatalog.suggestedNextAction}
              </div>
            )}
          </div>
        </Banner>
      )}

      {retryState.kind === 'error' && (
        <Banner tone="err">{retryState.message}</Banner>
      )}

      <div
        style={{
          display: 'flex',
          gap: 'var(--sp-4)',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        <Button variant="ghost" onClick={() => setAbortOpen(true)}>
          Abort &amp; close cart
        </Button>
        <Button
          variant="primary"
          onClick={() => { void handleRetry(); }}
          disabled={retryState.kind === 'submitting'}
        >
          {retryState.kind === 'submitting' ? 'Retrying…' : 'Retry commit'}
        </Button>
      </div>

      {abortOpen && (
        <CloseCartModal
          open={abortOpen}
          cart={cart}
          onClose={() => setAbortOpen(false)}
          onResolved={(resolved) => {
            setAbortOpen(false);
            onAborted?.(resolved);
          }}
        />
      )}
    </section>
  );
}
